"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import { useState } from "react";
import { cn } from "@/lib/utils";
import Button from "@/components/ui/button";
import { SimpleSearchBar } from "@/components/search/simple-search-bar";
import dynamic from "next/dynamic";
import type { ParsedQuery } from "@/types/natural-language-search";
import {
  addRecentSearch,
  isPropertyId,
  extractPropertyId,
} from "@/lib/recent-searches";

// Lazy load modal since it's only shown when needed
const SignupRequiredModal = dynamic(() => import("@/components/auth/signup-required-modal").then(mod => ({ default: mod.SignupRequiredModal })), {
  ssr: false,
  loading: () => null
});

interface HomePageHeroProps {
  isAuthenticated: boolean;
}

/**
 * Home Page Hero Component
 * Features natural language search and system actions.
 * Fixed structural nesting and tag closures.
 */
export default function HomePageHero({ isAuthenticated }: HomePageHeroProps) {
  const router = useRouter();
  const [isSearching, setIsSearching] = useState(false);
  const [showSignupModal, setShowSignupModal] = useState(false);

  const handleNaturalLanguageSearch = async (
    query: string,
    parsedQuery: ParsedQuery,
  ) => {
    if (!query.trim()) return;

    // Show signup modal if not authenticated
    if (!isAuthenticated) {
      setShowSignupModal(true);
      return;
    }

    setIsSearching(true);
    try {
      // Check if query is a property ID (e.g., "USL-12345")
      if (isPropertyId(query)) {
        const propertyId = extractPropertyId(query);
        if (propertyId) {
          router.push(`/listing/${propertyId}`);
          return;
        }
      }

      addRecentSearch(query);
      router.push(`/search?q=${encodeURIComponent(query.trim())}`);
    } catch (error) {
      console.error("Search navigation error:", error);
    } finally {
      setIsSearching(false);
    }
  };

  return (
    <section className="relative min-h-[90vh] bg-transparent py-20">
      {/* Removed bg-background-primary to let the Monument show through */}

      <div className="md:py-15 relative z-20 mx-auto max-w-7xl px-4 py-3 sm:px-6 lg:px-8">
        <div className="text-center">
          {/* Enhanced heading */}
          <div className="px-8 py-3">
            <h1 className="mb-2 text-4xl font-bold text-white drop-shadow-[0_2px_15px_rgba(0,0,0,0.8)] md:text-5xl lg:text-6xl">
              The Platform to Promote
              <span
                className={cn(
                  "text-text-accent mt-2 block",
                  "saturate-150 filter",
                )}
                style={{
                  filter: "drop-shadow(0 0 20px var(--theme-primary-glow))",
                }}
              >
                Commercial Real Estate
              </span>
            </h1>
          </div>

          {/* Enhanced subtitle - Increased shadow for visibility against image */}
          <div className="px-6 py-4">
            <p className="mx-auto flex max-w-3xl items-center justify-center gap-4 text-xl font-medium text-white drop-shadow-[0_2px_10px_rgba(0,0,0,1)] md:gap-8 md:text-2xl">
              <span>Buy</span>
              <span className="text-primary-default">•</span>
              <span>Sell</span>
              <span className="text-primary-default">•</span>
              <span>Simple</span>
            </p>
          </div>

          {/* Natural Language Search Bar - Glassmorphism look */}
          <div className="mx-auto mb-8 w-full max-w-none px-0 sm:max-w-3xl sm:px-0">
            <SimpleSearchBar
              onSearch={handleNaturalLanguageSearch}
              isSearching={isSearching}
              placeholder='Try: "warehouse with loading docks near Dallas under 3 million"'
              className={cn(
                "bg-black/20 backdrop-blur-md", // Switched to light glass to show monument
                "border-border-default focus-within:border-border-focus hover:border-border-hover border-2",
                "shadow-theme-xl duration-theme-normal transition-all",
                "rounded-theme-none text-white placeholder:text-white/60",
              )}
              style={{
                boxShadow:
                  "0 0 40px var(--theme-primary-glow), 0 20px 60px rgba(0,0,0,0.4)",
              }}
            />
          </div>

          {/* System Entry Buttons */}
          <div className="mt-10 flex flex-col justify-center gap-6 sm:flex-row">
            {isAuthenticated ? (
              <Button asChild size="lg" className="usland-button">
                <Link href="/dashboard">USER DASHBOARD</Link>
              </Button>
            ) : (
              <Button
                onClick={() => setShowSignupModal(true)}
                size="lg"
                className="usland-button"
              >
                GET STARTED
              </Button>
            )}

            <Button asChild size="lg" className="usland-button">
              <Link href="/search">BROWSE LISTINGS</Link>
            </Button>
          </div>
        </div>
      </div>

      <SignupRequiredModal
        isOpen={showSignupModal}
        onClose={() => setShowSignupModal(false)}
      />
    </section>
  );
}
